//#region src/lib/format.ts
/**
* Formatting for money, dates and "last updated" times. Intl only; the console is en-US.
*
* Two kinds of date go through here, and they must not be mixed up:
*
* - A **calendar date** (a pay date) is a day on the calendar with no time and no time
*   zone. It is passed as `'YYYY-MM-DD'` or `{ year, month, day }` and is formatted
*   as-is, so 07/31 is 07/31 in every time zone. It is never a `Date`: `new
*   Date('2026-07-31')` is midnight UTC, which is still Jul 30 in the Americas.
* - An **instant** (an upload time, a "last updated" time) is a moment in time. It is
*   passed as a `Date`, an ISO timestamp or epoch milliseconds, and is shown in the
*   viewer's time zone unless `timeZone` says otherwise.
*
* Invalid input formats as {@link INVALID_VALUE} rather than "NaN" or "Invalid Date".
*/
var LOCALE = "en-US";
/** What the formatters return for missing or invalid input: an em dash. */
var INVALID_VALUE = "—";
var currencyFormat = new Intl.NumberFormat(LOCALE, {
	style: "currency",
	currency: "USD",
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
	signDisplay: "negative"
});
/** A plain decimal string, as the API sends money: "1234.5", "-0.25". */
var DECIMAL = /^-?\d+(\.\d+)?$/;
/**
* Formats an amount in US dollars: `$1,234.56`, `-$1,234.56`, `$0.00`.
*
* Negatives get a leading "-"; colouring them is up to the caller (or `Money`). Rounds
* half away from zero. The API's decimal strings can be passed as they are, and keep
* their precision beyond what a number holds.
*/
function formatCurrency(amount) {
	if (typeof amount === "number" && !Number.isFinite(amount)) return "—";
	if (typeof amount === "string" && !DECIMAL.test(amount.trim())) return "—";
	if (amount == null) return "—";
	const value = typeof amount === "string" ? amount.trim() : amount;
	return currencyFormat.format(value);
}
var percentFormat = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 2 });
/**
* Formats a percentage given as the percent number, as the API sends it: `15` is `15%`,
* `12.5` is `12.5%`, up to 2 decimals. Not a number: the em dash.
*/
function formatPercent(value) {
	if (value == null) return "—";
	if (typeof value === "string" && !DECIMAL.test(value.trim())) return "—";
	const number = Number(value);
	if (!Number.isFinite(number)) return "—";
	return `${percentFormat.format(number)}%`;
}
var ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Parses a pay date, or returns `null` if it isn't a real day (`2026-02-30`). */
function parseCalendarDate(input) {
	let date;
	if (typeof input === "string") {
		const match = ISO_DATE.exec(input.trim());
		if (!match) return null;
		date = {
			year: Number(match[1]),
			month: Number(match[2]),
			day: Number(match[3])
		};
	} else if (input && typeof input === "object") date = {
		year: input.year,
		month: input.month,
		day: input.day
	};
	else return null;
	const { year, month, day } = date;
	if (![
		year,
		month,
		day
	].every(Number.isInteger) || year < 1 || year > 9999) return null;
	const utc = new Date(Date.UTC(year, month - 1, day));
	utc.setUTCFullYear(year);
	if (utc.getUTCMonth() !== month - 1 || utc.getUTCDate() !== day) return null;
	return date;
}
function toInstant(input) {
	if (input == null || input === "") return null;
	const date = input instanceof Date ? input : new Date(input);
	return Number.isNaN(date.getTime()) ? null : date;
}
/** MM/DD/YYYY, from the parts of a 2-digit month and day and a numeric year. */
function formatDay(date, timeZone) {
	const parts = new Intl.DateTimeFormat(LOCALE, {
		timeZone,
		month: "2-digit",
		day: "2-digit",
		year: "numeric"
	}).formatToParts(date);
	const part = (type) => parts.find((p) => p.type === type)?.value ?? "";
	return `${part("month")}/${part("day")}/${part("year")}`;
}
var normalizeSpaces = (text) => text.replace(/[\u202f\u00a0]/g, " ");
function formatInstant(date, style, timeZone) {
	const day = formatDay(date, timeZone);
	if (style === "date") return day;
	const time = new Intl.DateTimeFormat(LOCALE, {
		timeZone,
		hour: "numeric",
		minute: "2-digit"
	});
	return normalizeSpaces(`${day}${style === "lastUpdated" ? " at " : ", "}${time.format(date)}`);
}
function formatDate(date, style = "payDate", options = {}) {
	if (style === "payDate") {
		const parsed = parseCalendarDate(date);
		if (!parsed) return "—";
		const utc = new Date(Date.UTC(parsed.year, parsed.month - 1, parsed.day));
		utc.setUTCFullYear(parsed.year);
		return formatDay(utc, "UTC");
	}
	const instant = toInstant(date);
	if (!instant) return "—";
	try {
		return formatInstant(instant, style, options.timeZone);
	} catch {
		return "—";
	}
}
var MINUTE = 6e4;
var HOUR = 60 * MINUTE;
var DAY = 24 * HOUR;
/**
* How long ago an instant was, for "Updated 5 min ago" (the caller adds "Updated"):
* `just now` under a minute, then `5 min ago`, `3 hr ago`, `2 days ago`; from a week on,
* and for times in the future beyond a minute of clock skew, the `lastUpdated` date.
*
* `now` is a parameter so the result is deterministic; re-render on a timer to keep it
* current.
*/
function formatRelative(date, now = /* @__PURE__ */ new Date(), options = {}) {
	const then = toInstant(date);
	const reference = toInstant(now);
	if (!then || !reference) return "—";
	const elapsed = reference.getTime() - then.getTime();
	if (elapsed < -6e4 || elapsed >= 7 * DAY) return formatDate(then, "lastUpdated", options);
	if (elapsed < MINUTE) return "just now";
	if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)} min ago`;
	if (elapsed < DAY) return `${Math.floor(elapsed / HOUR)} hr ago`;
	const days = Math.floor(elapsed / DAY);
	return days === 1 ? "1 day ago" : `${days} days ago`;
}
//#endregion
export { INVALID_VALUE, formatCurrency, formatDate, formatPercent, formatRelative, parseCalendarDate };

//# sourceMappingURL=format.js.map